<template>
    <div ref="containerRef" class="resizable-workspace-split" :class="{ dragging: isDragging }">
        <div class="split-pane split-pane--panel" :style="panelStyle">
            <slot name="panel" />
        </div>

        <button
            type="button"
            class="split-handle"
            :class="{ 'split-handle--collapsed': collapsed }"
            :aria-hidden="collapsed"
            :tabindex="collapsed ? -1 : 0"
            :aria-label="handleAriaLabel"
            @pointerdown="startDragging"
        >
            <span class="split-handle__grip"></span>
        </button>

        <div class="split-pane split-pane--content">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useResizablePane } from '~/composables/useResizablePane'

const props = withDefaults(
    defineProps<{
        size: number
        collapsed?: boolean
        minPanelPx?: number
        maxPanelPx?: number
        minContentPx?: number
        collapsedPanelPx?: number
        handleAriaLabel?: string
    }>(),
    {
        collapsed: false,
        minPanelPx: 280,
        maxPanelPx: 424,
        minContentPx: 680,
        collapsedPanelPx: 0,
        handleAriaLabel: 'Resize workspace panel',
    }
)

const emit = defineEmits<{
    'update:size': [value: number]
}>()

const containerRef = ref<HTMLElement | null>(null)
const sizePercent = ref(props.size)
/* 与各工具页传入的 min-panel-px（多为 280）一致，勿再抬到 300，否则左侧创作栏会莫名变宽 */
const effectiveMinPanelPx = computed(() => props.minPanelPx)

watch(
    () => props.size,
    value => {
        sizePercent.value = value
    }
)

const { isDragging, startDragging } = useResizablePane({
    containerRef,
    sizePercent,
    minPanePx: effectiveMinPanelPx.value,
    maxPanePx: props.maxPanelPx,
    minContentPx: props.minContentPx,
    onChange: value => emit('update:size', value),
})

const collapsed = toRef(props, 'collapsed')

const panelStyle = computed(() => {
    if (collapsed.value) {
        return {
            '--split-panel-size': `${props.collapsedPanelPx}px`,
            '--split-panel-min-width': '0px',
            '--split-panel-max-width': `${props.collapsedPanelPx}px`,
        }
    }

    return {
        '--split-panel-size': `${sizePercent.value}%`,
        '--split-panel-min-width': `${effectiveMinPanelPx.value}px`,
        '--split-panel-max-width': `${props.maxPanelPx}px`,
    }
})
</script>

<style scoped lang="less">
@split-ease: cubic-bezier(0.33, 0.86, 0.2, 1);
@split-dur: 0.48s;

.resizable-workspace-split {
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
}

.split-pane {
    min-height: 0;
    min-width: 0;
}

.split-pane--panel,
.split-pane--content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
}

.split-pane--panel > :first-child,
.split-pane--content > :first-child {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
}

@media (min-width: 1024px) {
    .resizable-workspace-split {
        flex-direction: row;
        align-items: stretch;
    }

    /* 收起时略慢、末端更柔，避免内容区瞬间被「抽走」的生硬感 */
    .split-pane--panel {
        flex: 0 0 var(--split-panel-size);
        width: var(--split-panel-size);
        min-width: var(--split-panel-min-width, 0px);
        max-width: var(--split-panel-max-width);
        position: relative;
        /* visible：收起条、下拉等需溢出面板边界 */
        overflow: visible;
        z-index: 2;
        transition:
            flex-basis @split-dur @split-ease,
            width @split-dur @split-ease,
            min-width @split-dur @split-ease,
            max-width @split-dur @split-ease;
    }

    .resizable-workspace-split.dragging .split-pane--panel {
        transition: none;
    }

    .split-pane--content {
        flex: 1 1 auto;
        min-height: 0;
    }

    /* 与分栏同速，避免各页 .creator-panel-wrapper 写死 0.3s 与分栏 0.48s 错位导致收起发涩 */
    .split-pane--panel :deep(> .creator-panel-wrapper) {
        transition: width @split-dur @split-ease;
    }

    .resizable-workspace-split.dragging .split-pane--panel :deep(> .creator-panel-wrapper) {
        transition: none;
    }

    /* 各工具页在 is-collapsed 时对 > .flex 设了 opacity:0 / width:0，但未写 transition，这里统一补上 */
    .split-pane--panel :deep(.creator-panel-wrapper > .flex) {
        transition:
            opacity @split-dur @split-ease,
            width @split-dur @split-ease,
            max-width @split-dur @split-ease,
            padding @split-dur @split-ease,
            margin @split-dur @split-ease;
    }

    .resizable-workspace-split.dragging .split-pane--panel :deep(.creator-panel-wrapper > .flex) {
        transition: none;
    }
}

.split-handle {
    display: none;
}

@media (min-width: 1024px) {
    .split-handle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        flex: 0 0 12px;
        min-width: 0;
        cursor: col-resize;
        background: transparent;
        border: none;
        padding: 0;
        position: relative;
        z-index: 1;
        overflow: hidden;
        opacity: 1;
        transition:
            flex-basis @split-dur @split-ease,
            width @split-dur @split-ease,
            opacity 0.32s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .split-handle--collapsed {
        flex: 0 0 0;
        width: 0;
        opacity: 0;
        cursor: default;
        pointer-events: none;
    }

    .resizable-workspace-split.dragging .split-handle {
        transition: none;
    }

    .split-handle__grip {
        width: 4px;
        height: 72px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.1);
        transition: background-color 0.2s ease;
        flex-shrink: 0;
    }

    .split-handle:hover .split-handle__grip,
    .dragging .split-handle__grip {
        background: rgba(153, 247, 104, 0.75);
    }
}

/* 只负责分栏，不改内部设置面板的 padding / 布局 */
.resizable-workspace-split :deep(.creator-panel-wrapper) {
    width: 100% !important;
    min-width: 0;
    max-width: none !important;
}

.resizable-workspace-split :deep(.creator-panel-wrapper.is-collapsed) {
    width: 100% !important;
}

@media (prefers-reduced-motion: reduce) {
    @media (min-width: 1024px) {
        .resizable-workspace-split .split-pane--panel,
        .resizable-workspace-split .split-handle,
        .resizable-workspace-split .split-pane--panel :deep(> .creator-panel-wrapper),
        .resizable-workspace-split .split-pane--panel :deep(.creator-panel-wrapper > .flex) {
            transition: none;
        }
    }
}
</style>
